import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import JackpotPoolsBanner, { jackpotPoolsFromItem } from '../components/JackpotPoolsBanner';
import { formatEventDateGmt } from '../utils/eventDate';
import { effectiveEventStatus } from '../utils/eventOpen';
import { fetchPollImpliedBatch, rankPollOptionsByImplied } from '../utils/pollImplied';
import {
  normalizeSponsoredImages,
  normalizeSponsoredImageEntry,
} from '../utils/sponsoredImages';
import { ProductCard } from '../components/store/StoreUi';
import EventListToolbar from '../components/EventListToolbar';
import EventListPagination, {
  sliceEventPage,
  useEventListPage,
} from '../components/EventListPagination';
import {
  cupsFromEvents,
  eventKind,
  filterAndSortEvents,
  useEventListFilters,
} from '../utils/eventListFilter';

const CARD_BASE =
  'group relative flex flex-col h-full rounded-xl border bg-white dark:bg-gray-900 ' +
  'border-gray-200/80 dark:border-gray-700/80 shadow-sm ' +
  'hover:shadow-lg hover:border-red-300/70 dark:hover:border-red-800/50 ' +
  'transition-all duration-300 ease-out hover:-translate-y-1 overflow-hidden ' +
  'motion-safe:will-change-transform';

const CARD_INNER = 'flex flex-col h-full p-4 sm:p-5';

const CARD_ACCENT =
  'absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-red-600 via-red-500 to-red-400 ' +
  'opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-x-75 group-hover:scale-x-100 origin-center';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(Boolean(mq.matches));
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);
  return reduced;
}

/** Scroll-triggered fade/slide — never leaves content blank on mobile or tall lists. */
function Reveal({ children, className = '', delayMs = 0, as: Tag = 'div', id }) {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (reduced) {
      setVisible(true);
      return undefined;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const inView = el.getBoundingClientRect().top < (window.innerHeight || 800) * 0.95;
    if (inView) {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0, rootMargin: '120px 0px 120px 0px' }
    );
    io.observe(el);
    const fallback = window.setTimeout(() => setVisible(true), 600);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [reduced]);

  return (
    <Tag
      id={id}
      ref={ref}
      className={`home-reveal ${visible ? 'home-reveal-in' : ''} ${className}`}
      style={delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}

const statusBadgeClass = (status) => {
  if (status === 'upcoming') return 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800';
  if (status === 'live' || status === 'active') return 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800';
  if (status === 'locked') return 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800';
  if (status === 'completed') return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/50 dark:text-slate-200 dark:border-slate-700';
  return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600';
};

const actionBtnClass = (variant) => {
  const base = 'flex-1 min-w-[4.5rem] px-3 py-2.5 rounded-lg text-center text-xs sm:text-sm font-semibold transition-colors';
  if (variant === 'free') return `${base} bg-emerald-600 text-white hover:bg-emerald-700`;
  if (variant === 'boost') return `${base} bg-red-600 text-white hover:bg-red-700`;
  return `${base} border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:border-red-500 hover:text-red-700 dark:hover:border-red-500 dark:hover:text-red-400 bg-gray-50 dark:bg-gray-800/80`;
};

const cupSlugOf = (item) => {
  if (!item) return null;
  if (typeof item.cup === 'object' && item.cup?.slug) return item.cup.slug;
  return item.cupSlug || null;
};

const cupNameOf = (item) => {
  if (!item) return null;
  if (typeof item.cup === 'object' && item.cup?.name) return item.cup.name;
  return item.cupName || null;
};

const stageNameOf = (item) => {
  if (!item) return null;
  if (item.stageName) return item.stageName;
  if (typeof item.stage === 'object' && item.stage?.name) return item.stage.name;
  return null;
};

const Home = ({
  heroFeaturedMatch = null,
  heroFeaturedMatchId = null,
  landingResolved = true,
}) => {
  const [, setStatusClock] = useState(() => Date.now());
  const [cups, setCups] = useState([]);
  const [matches, setMatches] = useState([]);
  const [polls, setPolls] = useState([]);
  const [topStreaks, setTopStreaks] = useState([]);
  const [topLeaderboard, setTopLeaderboard] = useState([]);
  const [competeLoading, setCompeteLoading] = useState(true);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [storeLoading, setStoreLoading] = useState(true);
  const [pollImpliedByMarketId, setPollImpliedByMarketId] = useState({});
  const [loading, setLoading] = useState(true);

  // Keep scheduled status badges current even when the page stays open across
  // an event's start time. This is display-only and does not sign a contract tx.
  useEffect(() => {
    const timer = window.setInterval(() => setStatusClock(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const scrollToTournaments = (event) => {
    event.preventDefault();
    const section = document.getElementById('tournaments');
    if (!section) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(null, '', '#tournaments');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const listTimeout = 12000;
        const [cupsRes, matchesRes, pollsRes] = await Promise.allSettled([
          api.get('/cups', { timeout: listTimeout }),
          api.get('/matches', { timeout: listTimeout }),
          api.get('/polls', { timeout: listTimeout }),
        ]);
        if (cancelled) return;
        setCups(cupsRes.status === 'fulfilled' ? cupsRes.value.data || [] : []);
        setMatches(matchesRes.status === 'fulfilled' ? matchesRes.value.data || [] : []);
        setPolls(pollsRes.status === 'fulfilled' ? pollsRes.value.data || [] : []);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStoreLoading(true);
      try {
        const { data } = await api.get('/store/products', {
          params: { featured: true, limit: 10, sort: 'newest' },
        });
        if (!cancelled) {
          setFeaturedProducts(Array.isArray(data?.items) ? data.items : []);
        }
      } catch (error) {
        console.error('Error fetching featured store products:', error);
        if (!cancelled) setFeaturedProducts([]);
      } finally {
        if (!cancelled) setStoreLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCompeteLoading(true);
      try {
        const [leaderboardRes, streaksRes] = await Promise.allSettled([
          api.get('/leaderboard', { params: { type: 'all', page: 1, limit: 10 } }),
          api.get('/streaks', { params: { limit: 10 } }),
        ]);
        if (cancelled) return;

        const lbData = leaderboardRes.status === 'fulfilled' ? leaderboardRes.value.data : null;
        const lbUsers = Array.isArray(lbData?.users)
          ? lbData.users
          : Array.isArray(lbData)
            ? lbData
            : [];
        setTopLeaderboard(lbUsers.slice(0, 10));

        const streakData = streaksRes.status === 'fulfilled' ? streaksRes.value.data : null;
        const streakRows = Array.isArray(streakData) ? streakData : [];
        setTopStreaks(streakRows.slice(0, 10));
      } catch (error) {
        console.error('Error fetching compete data:', error);
        if (!cancelled) {
          setTopLeaderboard([]);
          setTopStreaks([]);
        }
      } finally {
        if (!cancelled) setCompeteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedHeroMatch = useMemo(() => {
    const id = heroFeaturedMatch?._id || heroFeaturedMatchId;
    if (!id && !heroFeaturedMatch) return null;
    const fromList = id
      ? matches.find((m) => String(m._id) === String(id))
      : null;
    if (heroFeaturedMatch?._id) {
      return fromList ? { ...heroFeaturedMatch, ...fromList } : heroFeaturedMatch;
    }
    return fromList || null;
  }, [heroFeaturedMatch, heroFeaturedMatchId, matches]);

  const featuredItems = useMemo(
    () => [...matches.filter((m) => m.isFeatured), ...polls.filter((p) => p.isFeatured)],
    [matches, polls]
  );

  const featuredFilters = useEventListFilters({ defaultStatus: 'all', defaultKind: 'all' });
  const matchFilters = useEventListFilters({ defaultStatus: 'all', defaultKind: 'match' });
  const pollFilters = useEventListFilters({ defaultStatus: 'all', defaultKind: 'poll' });

  const featuredCups = useMemo(() => cupsFromEvents(featuredItems), [featuredItems]);
  const matchCups = useMemo(() => cupsFromEvents(matches), [matches]);
  const pollCups = useMemo(() => cupsFromEvents(polls), [polls]);

  const filteredFeatured = useMemo(
    () => filterAndSortEvents(featuredItems, featuredFilters.filters),
    [featuredItems, featuredFilters.filters]
  );
  const filteredMatches = useMemo(
    () => filterAndSortEvents(matches, { ...matchFilters.filters, kind: 'match' }),
    [matches, matchFilters.filters]
  );
  const filteredPolls = useMemo(
    () => filterAndSortEvents(polls, { ...pollFilters.filters, kind: 'poll' }),
    [polls, pollFilters.filters]
  );

  const featuredFilterKey = useMemo(
    () => JSON.stringify(featuredFilters.filters),
    [featuredFilters.filters]
  );
  const matchFilterKey = useMemo(
    () => JSON.stringify(matchFilters.filters),
    [matchFilters.filters]
  );
  const pollFilterKey = useMemo(
    () => JSON.stringify(pollFilters.filters),
    [pollFilters.filters]
  );

  const featuredPage = useEventListPage(filteredFeatured.length, featuredFilterKey);
  const matchPage = useEventListPage(filteredMatches.length, matchFilterKey);
  const pollPage = useEventListPage(filteredPolls.length, pollFilterKey);

  const pagedFeatured = useMemo(
    () => sliceEventPage(filteredFeatured, featuredPage.page, featuredPage.pageSize),
    [filteredFeatured, featuredPage.page, featuredPage.pageSize]
  );
  const pagedMatches = useMemo(
    () => sliceEventPage(filteredMatches, matchPage.page, matchPage.pageSize),
    [filteredMatches, matchPage.page, matchPage.pageSize]
  );
  const pagedPolls = useMemo(
    () => sliceEventPage(filteredPolls, pollPage.page, pollPage.pageSize),
    [filteredPolls, pollPage.page, pollPage.pageSize]
  );

  const navbarCups = useMemo(
    () =>
      [...cups]
        .filter((c) => c.showInNavbar)
        .sort((a, b) => (a.navbarOrder || 0) - (b.navbarOrder || 0)),
    [cups]
  );

  const displayCups = navbarCups.length > 0 ? navbarCups : cups;

  useEffect(() => {
    const forImplied = [...featuredItems, ...polls];
    if (!forImplied.length) return;
    let cancelled = false;
    (async () => {
      const byMarketId = await fetchPollImpliedBatch(forImplied);
      if (!cancelled) setPollImpliedByMarketId(byMarketId);
    })();
    return () => {
      cancelled = true;
    };
  }, [featuredItems, polls]);

  const firstCupSlug = displayCups[0]?.slug;
  const showHeroSkeleton =
    !resolvedHeroMatch?._id &&
    (!landingResolved || Boolean(heroFeaturedMatchId && loading));
  const hasHeroFeatured = Boolean(resolvedHeroMatch?._id) || showHeroSkeleton;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 home-landing">
      {/* Hero — brand-led; optional featured match on the right */}
      <section className="relative overflow-hidden border-b border-stone-800/40 home-hero">
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          aria-hidden
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 80% 60% at 70% 20%, rgba(185,28,28,0.35), transparent 55%),' +
                'radial-gradient(ellipse 60% 50% at 10% 80%, rgba(28,25,23,0.9), transparent 50%),' +
                'linear-gradient(145deg, #1c1917 0%, #292524 45%, #450a0a 100%)',
            }}
          />
          <div className="home-hero-orb home-hero-orb-a" />
          <div className="home-hero-orb home-hero-orb-b" />
          <div
            className="absolute inset-0 opacity-[0.07] home-hero-grid"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div
            className={
              hasHeroFeatured
                ? 'grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 xl:gap-16 items-center'
                : ''
            }
          >
            <div className={hasHeroFeatured ? 'max-w-xl' : ''}>
            <h1
                className="home-hero-in home-hero-in-1 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-3"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '-0.02em' }}
              >
                <span className="home-brand-shine">WE R GAME</span>
              </h1>
              <p className="home-hero-in home-hero-in-2 text-lg sm:text-xl text-stone-200 max-w-xl leading-relaxed mb-8 font-medium">
                Sports information markets and fan gaming. Play FREE, BOOST and MARKET, follow live
                sports, earn Golden Tickets and compete for rewards.
              </p>
              <div className="home-hero-in home-hero-in-3 flex flex-wrap gap-3">
                {displayCups.length > 0 || loading ? (
                  <a
                    href="#tournaments"
                    onClick={scrollToTournaments}
                    className="home-cta-primary px-6 py-3 bg-red-600 text-white rounded-lg font-semibold text-sm sm:text-base"
                  >
                    Browse tournaments
                  </a>
                ) : null}
                <Link
                  to="/jackpot"
                  className="home-cta-ghost px-6 py-3 border border-stone-400/50 text-stone-100 rounded-lg font-semibold text-sm sm:text-base"
                >
                  Jackpots
                </Link>
                <Link
                  to="/leaderboard"
                  className="home-cta-ghost px-6 py-3 border border-stone-400/50 text-stone-100 rounded-lg font-semibold text-sm sm:text-base"
                >
                  Leaderboard
                </Link>
              </div>
            </div>

            {hasHeroFeatured ? (
              <div className="home-hero-in home-hero-in-4 w-full min-w-0 max-w-md mx-auto lg:max-w-none lg:mx-0 lg:justify-self-end">
                {resolvedHeroMatch?._id ? (
                  <HeroFeaturedMatchCard match={resolvedHeroMatch} />
                ) : (
                  <HeroFeaturedMatchSkeleton />
                )}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-12">
        {loading || displayCups.length > 0 ? (
          <Reveal as="section" id="tournaments" className="scroll-mt-24">
            <SectionHeader title="Tournaments" subtitle="Jump into any cup on the platform" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {loading && displayCups.length === 0
                ? [0, 1, 2, 3].map((i) => <CupChipSkeleton key={`cup-sk-${i}`} />)
                : displayCups.map((cup, i) => (
                <Link
                  key={cup._id}
                  to={`/cup/${cup.slug}`}
                  className="home-cup-chip group flex items-center gap-3 min-w-0 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-400 dark:hover:border-red-700 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
                  style={{ transitionDelay: `${Math.min(i, 8) * 40}ms` }}
                >
                  {cup.thumbnailImage ? (
                    <img
                      src={cup.thumbnailImage}
                      alt=""
                      className="w-11 h-11 rounded-lg object-cover border border-gray-100 dark:border-gray-800 transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-red-600 font-bold text-sm transition-transform duration-300 group-hover:scale-105">
                      {(cup.name || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white truncate group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                      {cup.name}
                    </div>
                    {cup.status ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{cup.status}</div>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          </Reveal>
        ) : null}

        {featuredItems.length > 0 && (
          <Reveal as="section" id="featured" className="scroll-mt-24">
            <SectionHeader title="Featured" subtitle="Highlighted matches and polls across all cups" />
            <EventListToolbar
              filters={featuredFilters.filters}
              onKindChange={featuredFilters.setKind}
              onStatusChange={featuredFilters.setStatus}
              onCupChange={featuredFilters.setCup}
              onSortChange={featuredFilters.setSort}
              onQueryChange={featuredFilters.setQuery}
              onReset={featuredFilters.reset}
              isActive={featuredFilters.isActive}
              showKind
              cups={featuredCups}
              resultCount={filteredFeatured.length}
              totalCount={featuredItems.length}
              searchPlaceholder="Search featured games"
            />
            {filteredFeatured.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                  {pagedFeatured.map((item, i) =>
                    eventKind(item) === 'match' ? (
                      <div key={item._id} className="home-stagger" style={{ animationDelay: `${i * 55}ms` }}>
                        <MatchCard match={item} featured />
                      </div>
                    ) : (
                      <div key={item._id} className="home-stagger" style={{ animationDelay: `${i * 55}ms` }}>
                        <PollCard poll={item} featured impliedByMarketId={pollImpliedByMarketId} />
                      </div>
                    )
                  )}
                </div>
                <EventListPagination
                  page={featuredPage.page}
                  pageCount={featuredPage.pageCount}
                  total={filteredFeatured.length}
                  pageSize={featuredPage.pageSize}
                  onPageChange={featuredPage.setPage}
                  scrollToId="featured"
                />
              </>
            ) : (
              <EmptyState message="No featured games match these filters." />
            )}
          </Reveal>
        )}

        <Reveal as="section" id="matches" className="scroll-mt-24">
          <SectionHeader
            title="Matches"
            subtitle="Fixtures from every tournament"
            action={firstCupSlug ? { to: `/cup/${firstCupSlug}`, label: 'Open a cup' } : null}
          />
          <EventListToolbar
            filters={matchFilters.filters}
            onKindChange={matchFilters.setKind}
            onStatusChange={matchFilters.setStatus}
            onCupChange={matchFilters.setCup}
            onSortChange={matchFilters.setSort}
            onQueryChange={matchFilters.setQuery}
            onReset={matchFilters.reset}
            isActive={matchFilters.isActive}
            cups={matchCups}
            resultCount={filteredMatches.length}
            totalCount={matches.length}
            searchPlaceholder="Search teams or cups"
          />
          {loading ? (
            <EventCardsSkeleton />
          ) : filteredMatches.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                {pagedMatches.map((match, i) => (
                  <div key={match._id} className="home-stagger" style={{ animationDelay: `${Math.min(i, 12) * 45}ms` }}>
                    <MatchCard match={match} />
                  </div>
                ))}
              </div>
              <EventListPagination
                page={matchPage.page}
                pageCount={matchPage.pageCount}
                total={filteredMatches.length}
                pageSize={matchPage.pageSize}
                onPageChange={matchPage.setPage}
                scrollToId="matches"
              />
            </>
          ) : (
            <EmptyState message="No matches match these filters. Try All or another tournament." />
          )}
        </Reveal>

        <Reveal as="section" id="polls" className="scroll-mt-24">
          <SectionHeader title="Polls" subtitle="Award and opinion markets across cups" />
          <EventListToolbar
            filters={pollFilters.filters}
            onKindChange={pollFilters.setKind}
            onStatusChange={pollFilters.setStatus}
            onCupChange={pollFilters.setCup}
            onSortChange={pollFilters.setSort}
            onQueryChange={pollFilters.setQuery}
            onReset={pollFilters.reset}
            isActive={pollFilters.isActive}
            cups={pollCups}
            resultCount={filteredPolls.length}
            totalCount={polls.length}
            searchPlaceholder="Search polls"
          />
          {loading ? (
            <EventCardsSkeleton />
          ) : filteredPolls.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                {pagedPolls.map((poll, i) => (
                  <div key={poll._id} className="home-stagger" style={{ animationDelay: `${Math.min(i, 12) * 45}ms` }}>
                    <PollCard poll={poll} impliedByMarketId={pollImpliedByMarketId} />
                  </div>
                ))}
              </div>
              <EventListPagination
                page={pollPage.page}
                pageCount={pollPage.pageCount}
                total={filteredPolls.length}
                pageSize={pollPage.pageSize}
                onPageChange={pollPage.setPage}
                scrollToId="polls"
              />
            </>
          ) : (
            <EmptyState message="No polls match these filters. Try All or another tournament." />
          )}
        </Reveal>

        <Reveal as="section">
          <SectionHeader title="Compete" subtitle="Climb the board and keep your streak alive" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <QuickLink to="/leaderboard" title="Leaderboard" desc="Rankings by points and wins" />
            <QuickLink to="/streaks" title="Streaks" desc="Hottest prediction runs" />
            <QuickLink to="/jackpot" title="Jackpots" desc="Free and Boost pools" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              <SectionHeader
                title="Leaderboard"
                subtitle="Top 10 by points"
                action={{ to: '/leaderboard', label: 'See all' }}
              />
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden transition-shadow duration-300 hover:shadow-md">
                {competeLoading ? (
                  <div className="py-12 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-600 border-t-transparent" />
                  </div>
                ) : topLeaderboard.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-800 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          <th className="px-4 py-3 font-semibold">Rank</th>
                          <th className="px-4 py-3 font-semibold">Player</th>
                          <th className="px-4 py-3 font-semibold text-right">Points</th>
                          <th className="px-4 py-3 font-semibold text-right hidden sm:table-cell">Wins</th>
                          <th className="px-4 py-3 font-semibold text-right hidden md:table-cell">Win rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {topLeaderboard.map((user, index) => {
                          const wins = Number(user.correctPredictions) || 0;
                          const total = Number(user.totalPredictions) || 0;
                          const winRate = total > 0 ? ((wins / total) * 100).toFixed(0) : '0';
                          const points = Number(user.points) || 0;
                          return (
                            <tr
                              key={user._id || `lb-${index}`}
                              className="hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex w-7 h-7 items-center justify-center rounded-full text-xs font-bold ${
                                    index === 0
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'
                                      : index === 1
                                        ? 'bg-stone-200 text-stone-700 dark:bg-stone-700 dark:text-stone-200'
                                        : index === 2
                                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200'
                                          : 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300'
                                  }`}
                                >
                                  {index + 1}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-full bg-stone-800 text-white text-xs font-bold flex items-center justify-center shrink-0">
                                    {(user.username || 'P').slice(0, 1).toUpperCase()}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-semibold text-gray-900 dark:text-white truncate">
                                      {user.username || 'Player'}
                                    </div>
                                    {user.streak > 0 ? (
                                      <div className="text-[11px] text-red-600 dark:text-red-400 font-medium tabular-nums">
                                        Streak {user.streak}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right font-bold tabular-nums text-gray-900 dark:text-white">
                                {Number.isInteger(points) ? points : points.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-600 dark:text-gray-300 hidden sm:table-cell">
                                {wins}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-600 dark:text-gray-300 hidden md:table-cell">
                                {winRate}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-10">
                    No leaderboard data yet
                  </p>
                )}
                <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 flex justify-end">
                  <Link
                    to="/leaderboard"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 group"
                  >
                    See all leaderboard
                    <svg
                      className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <SectionHeader
                title="Top streaks"
                subtitle="Top 10 hottest runs"
                action={{ to: '/streaks', label: 'View all' }}
              />
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden transition-shadow duration-300 hover:shadow-md">
                {competeLoading ? (
                  <div className="py-12 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-600 border-t-transparent" />
                  </div>
                ) : topStreaks.length > 0 ? (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {topStreaks.map((user, index) => {
                      const streakVal =
                        Number(user.currentStreak) ||
                        Number(user.streak) ||
                        0;
                      const best =
                        Number(user.longestStreak) ||
                        Number(user.bestStreak) ||
                        0;
                      return (
                        <li
                          key={user._id || `st-${index}`}
                          className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                                index === 0
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'
                                  : 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200'
                              }`}
                            >
                              {index + 1}
                            </span>
                            <div className="min-w-0">
                              <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                {user.username || 'Player'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                best {best}
                              </div>
                            </div>
                          </div>
                          <span className="text-base font-black text-red-600 dark:text-red-400 tabular-nums shrink-0">
                            {streakVal}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-10">
                    No streak data yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </Reveal>

        {(storeLoading || featuredProducts.length > 0) && (
          <Reveal as="section" className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-amber-200/70 dark:border-amber-500/20 bg-gradient-to-br from-amber-50 via-white to-orange-50/70 dark:from-amber-950/25 dark:via-gray-900 dark:to-orange-950/20 p-5 sm:p-7">
              <div
                className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-amber-300/20 blur-3xl dark:bg-amber-500/10"
                aria-hidden
              />
              <div className="relative">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">
                      Golden Ticket marketplace
                    </p>
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-950 dark:text-white">
                      Featured rewards
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      Turn the Golden Tickets you earn into jerseys, sneakers, and exclusive gear.
                    </p>
                  </div>
                  <Link
                    to="/store"
                    className="group inline-flex shrink-0 items-center gap-2 self-start rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-amber-900/10 transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-600 dark:bg-white dark:text-gray-950 dark:hover:bg-amber-400 sm:self-auto"
                  >
                    Explore the store
                    <span className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden>
                      →
                    </span>
                  </Link>
                </div>

                <div
                  className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3 pt-1"
                  aria-label="Featured Golden Store products"
                >
                  {storeLoading
                    ? Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={index}
                          className="w-[78vw] max-w-[280px] shrink-0 snap-start overflow-hidden rounded-2xl border border-amber-100 dark:border-white/10 bg-white dark:bg-gray-900"
                        >
                          <div className="aspect-square animate-pulse bg-amber-100/70 dark:bg-gray-800" />
                          <div className="space-y-3 p-4">
                            <div className="h-3 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                            <div className="h-5 w-4/5 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                            <div className="h-7 w-24 animate-pulse rounded bg-amber-100 dark:bg-amber-900/30" />
                          </div>
                        </div>
                      ))
                    : featuredProducts.map((product, index) => (
                        <div
                          key={product._id}
                          className="home-stagger w-[78vw] max-w-[280px] shrink-0 snap-start"
                          style={{ animationDelay: `${index * 65}ms` }}
                        >
                          <ProductCard product={product} />
                        </div>
                      ))}
                </div>

                {!storeLoading && featuredProducts.length > 3 && (
                  <p className="mt-1 text-center text-[11px] font-medium text-amber-800/70 dark:text-amber-300/60 sm:text-left">
                    Swipe or scroll to discover more featured rewards.
                  </p>
                )}
              </div>
            </div>
          </Reveal>
        )}
      </div>

      <style>{`
        .home-landing {
          overflow-anchor: none;
        }
        .home-hero {
          contain: layout paint;
        }
        @keyframes homeFadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes homeOrbDrift {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(2%, -3%) scale(1.08); opacity: 0.85; }
        }
        @keyframes homeOrbDriftAlt {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate(-3%, 2%) scale(1.12); opacity: 0.7; }
        }
        @keyframes homeGridDrift {
          from { background-position: 0 0; }
          to { background-position: 60px 60px; }
        }
        @keyframes homeBrandShine {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes homeLivePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
          70% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
        }
        .home-hero-orb {
          position: absolute;
          border-radius: 9999px;
          pointer-events: none;
          filter: blur(32px);
        }
        .home-hero-orb-a {
          width: 42vw;
          max-width: 520px;
          height: 42vw;
          max-height: 520px;
          top: -12%;
          right: 8%;
          background: radial-gradient(circle, rgba(220,38,38,0.45), transparent 68%);
          animation: homeOrbDrift 12s ease-in-out infinite;
        }
        .home-hero-orb-b {
          width: 36vw;
          max-width: 420px;
          height: 36vw;
          max-height: 420px;
          bottom: -18%;
          left: -4%;
          background: radial-gradient(circle, rgba(120,53,15,0.5), transparent 70%);
          animation: homeOrbDriftAlt 15s ease-in-out infinite;
        }
        .home-hero-grid {
          animation: homeGridDrift 28s linear infinite;
        }
        .home-hero-in {
          opacity: 0;
          animation: homeFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .home-hero-in-1 { animation-delay: 0.05s; }
        .home-hero-in-2 { animation-delay: 0.16s; }
        .home-hero-in-3 { animation-delay: 0.28s; }
        .home-hero-in-4 { animation-delay: 0.22s; }
        .home-brand-shine {
          background: linear-gradient(105deg, #fff 35%, #fecaca 48%, #fff 62%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: homeBrandShine 6s ease-in-out infinite;
        }
        .home-cta-primary {
          position: relative;
          overflow: hidden;
          transition: transform 0.25s ease, background-color 0.2s ease, box-shadow 0.25s ease;
          box-shadow: 0 8px 24px -10px rgba(220, 38, 38, 0.65);
        }
        .home-cta-primary:hover {
          transform: translateY(-2px);
          background-color: #ef4444;
          box-shadow: 0 12px 28px -10px rgba(220, 38, 38, 0.75);
        }
        .home-cta-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.28) 50%, transparent 60%);
          transform: translateX(-120%);
          transition: transform 0.55s ease;
        }
        .home-cta-primary:hover::after { transform: translateX(120%); }
        .home-cta-ghost {
          transition: transform 0.25s ease, border-color 0.2s ease, background-color 0.2s ease;
        }
        .home-cta-ghost:hover {
          transform: translateY(-2px);
          border-color: rgba(255,255,255,0.85);
          background-color: rgba(255,255,255,0.06);
        }
        .home-reveal {
          opacity: 1;
          transform: none;
          transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1), transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @media (min-width: 768px) {
          .home-reveal:not(.home-reveal-in) {
            opacity: 0;
            transform: translateY(18px);
          }
        }
        .home-reveal-in {
          opacity: 1;
          transform: translateY(0);
        }
        .home-reveal-in .home-stagger {
          animation: homeFadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (max-width: 767px) {
          .home-hero-in,
          .home-reveal,
          .home-reveal-in .home-stagger {
            opacity: 1 !important;
            transform: none !important;
            animation: none !important;
          }
          .home-hero-orb {
            display: none;
          }
        }
        .home-live-dot {
          animation: homeLivePulse 2s ease-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .home-hero-orb-a,
          .home-hero-orb-b,
          .home-hero-grid,
          .home-brand-shine,
          .home-live-dot,
          .home-hero-in,
          .home-reveal-in .home-stagger {
            animation: none !important;
          }
          .home-hero-in,
          .home-reveal {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
          .home-cta-primary:hover,
          .home-cta-ghost:hover {
            transform: none;
          }
        }
      `}</style>
    </div>
  );
};

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="flex items-end justify-between gap-4 mb-4">
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{title}</h2>
      {subtitle ? <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p> : null}
    </div>
    {action ? (
      <Link
        to={action.to}
        className="shrink-0 text-sm font-semibold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
      >
        {action.label}
      </Link>
    ) : null}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 py-12 text-center text-gray-500 dark:text-gray-400 text-sm">
    {message}
  </div>
);

const CupChipSkeleton = () => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 animate-pulse">
    <div className="w-11 h-11 rounded-lg bg-gray-200 dark:bg-gray-800 shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="h-3.5 w-28 rounded bg-gray-200 dark:bg-gray-800" />
      <div className="h-2.5 w-16 rounded bg-gray-100 dark:bg-gray-800/80" />
    </div>
  </div>
);

const EventCardsSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
    {[0, 1, 2].map((i) => (
      <div
        key={`event-sk-${i}`}
        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 sm:p-5 animate-pulse"
      >
        <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-800 mb-4" />
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-800" />
          <div className="flex-1 h-4 rounded bg-gray-200 dark:bg-gray-800" />
          <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-800" />
        </div>
        <div className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800/80 mb-3" />
        <div className="flex gap-2">
          <div className="flex-1 h-9 rounded-lg bg-gray-200 dark:bg-gray-800" />
          <div className="flex-1 h-9 rounded-lg bg-gray-200 dark:bg-gray-800" />
          <div className="flex-1 h-9 rounded-lg bg-gray-200 dark:bg-gray-800" />
        </div>
      </div>
    ))}
  </div>
);

const HeroFeaturedMatchSkeleton = () => (
  <div
    className="rounded-2xl border border-white/15 bg-stone-950/88 p-4 sm:p-5 animate-pulse"
    aria-hidden
  >
    <div className="h-5 w-24 rounded-md bg-white/10 mb-4" />
    <div className="flex items-center gap-3 sm:gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-white/10 shrink-0" />
      <div className="flex-1 h-5 rounded bg-white/10" />
      <div className="w-8 h-3 rounded bg-white/10 shrink-0" />
      <div className="flex-1 h-5 rounded bg-white/10" />
      <div className="w-12 h-12 rounded-full bg-white/10 shrink-0" />
    </div>
    <div className="h-3 w-40 rounded bg-white/10 mb-4" />
    <div className="h-16 rounded-xl bg-white/10 mb-4" />
    <div className="flex gap-2">
      <div className="flex-1 h-10 rounded-lg bg-white/10" />
      <div className="flex-1 h-10 rounded-lg bg-white/10" />
      <div className="flex-1 h-10 rounded-lg bg-white/10" />
    </div>
  </div>
);

function HeroTeamMark({ src, name }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || '?').slice(0, 2).toUpperCase();
  if (!src || failed) {
    return (
      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center text-white/80 text-xs font-bold flex-shrink-0">
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={48}
      height={48}
      decoding="async"
      fetchPriority="high"
      onError={() => setFailed(true)}
      className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-white/20 bg-white/10"
    />
  );
}

const QuickLink = ({ to, title, desc }) => (
  <Link
    to={to}
    className="block rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 hover:border-red-400 dark:hover:border-red-700 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group"
  >
    <div className="font-semibold text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
      {title}
    </div>
    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{desc}</div>
  </Link>
);

const CupChip = ({ item }) => {
  const name = cupNameOf(item);
  const slug = cupSlugOf(item);
  if (!name) return null;
  const inner = (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 bg-stone-50 dark:bg-stone-900/60">
      {name}
    </span>
  );
  return slug ? (
    <Link to={`/cup/${slug}`} onClick={(e) => e.stopPropagation()} className="hover:opacity-80">
      {inner}
    </Link>
  ) : (
    inner
  );
};

const CardActions = ({ freeEnabled, marketEnabled, freeTo, boostTo, marketTo }) => (
  <div className="mt-auto flex flex-wrap gap-2 pt-1">
    {freeEnabled ? (
      <Link to={freeTo} className={actionBtnClass('free')}>
        Free
      </Link>
    ) : null}
    <Link to={boostTo} className={actionBtnClass('boost')}>
      Boost
    </Link>
    {marketEnabled !== false ? (
      <Link to={marketTo} className={actionBtnClass('market')}>
        Market
      </Link>
    ) : null}
  </div>
);

const HeroFeaturedMatchCard = ({ match }) => {
  if (!match?._id) return null;

  const freeEnabled = match.freePredictionEnabled !== false;
  const marketEnabled = match.marketEnabled !== false;
  const { freeJackpot, boostJackpot } = jackpotPoolsFromItem(match);
  const status = effectiveEventStatus(match);
  const sponsoredImages = match.isSponsored
    ? normalizeSponsoredImages(match.sponsoredImages)
    : [];
  const primarySponsor = sponsoredImages[0] ? normalizeSponsoredImageEntry(sponsoredImages[0]) : null;

  return (
    <div className="space-y-3">
      <article className="relative overflow-hidden rounded-2xl border border-white/15 bg-stone-950/90 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.65)]">
        <div
          className="absolute inset-0 opacity-80 pointer-events-none"
          style={{
            background:
              'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, transparent 42%), radial-gradient(ellipse at 100% 0%, rgba(220,38,38,0.22), transparent 55%)',
          }}
          aria-hidden
        />
        <div className="relative p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-[0.14em] bg-red-600 text-white shadow-sm shadow-red-900/40">
                Featured
              </span>
              {match.isSponsored ? (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-amber-400/40 text-amber-200 bg-amber-500/10">
                  Sponsored
                </span>
              ) : null}
              {cupNameOf(match) ? (
                cupSlugOf(match) ? (
                  <Link
                    to={`/cup/${cupSlugOf(match)}`}
                    className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border border-white/20 text-stone-200 bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    {cupNameOf(match)}
                  </Link>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border border-white/20 text-stone-200 bg-white/5">
                    {cupNameOf(match)}
                  </span>
                )
              ) : null}
            </div>
            <span
              className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border ${statusBadgeClass(
                status
              )}`}
            >
              {status || '—'}
            </span>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <HeroTeamMark src={match.teamAImage} name={match.teamA} />
              <span className="font-bold text-white truncate text-base sm:text-lg leading-tight">
                {match.teamA}
              </span>
            </div>
            <span className="text-[11px] font-black text-stone-400 uppercase shrink-0 tracking-widest">
              vs
            </span>
            <div className="flex items-center gap-2.5 flex-1 min-w-0 justify-end">
              <span className="font-bold text-white truncate text-base sm:text-lg leading-tight text-right">
                {match.teamB}
              </span>
              <HeroTeamMark src={match.teamBImage} name={match.teamB} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-300 mb-4">
            {stageNameOf(match) ? (
              <span className="font-medium text-stone-100">{stageNameOf(match)}</span>
            ) : null}
            {stageNameOf(match) ? <span className="text-stone-500">·</span> : null}
            <span className="tabular-nums">{formatEventDateGmt(match.date) || '—'}</span>
          </div>

          <div className="mb-4">
            <JackpotPoolsBanner
              freeJackpot={freeJackpot}
              boostJackpot={boostJackpot}
              compact
              className="border-white/10 from-emerald-950/50 to-stone-950/40 !bg-gradient-to-br"
            />
          </div>

          <div className="flex gap-2">
            {freeEnabled ? (
              <Link
                to={`/match/${match._id}/free`}
                className="flex-1 px-3 py-2.5 rounded-lg text-center text-xs sm:text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
              >
                Free
              </Link>
            ) : null}
            <Link
              to={`/match/${match._id}/boost`}
              className="flex-1 px-3 py-2.5 rounded-lg text-center text-xs sm:text-sm font-semibold bg-red-600 text-white hover:bg-red-500 transition-colors"
            >
              Boost
            </Link>
            {marketEnabled !== false ? (
              <Link
                to={`/match/${match._id}/market`}
                className="flex-1 px-3 py-2.5 rounded-lg text-center text-xs sm:text-sm font-semibold border border-white/25 text-white hover:bg-white/10 transition-colors"
              >
                Market
              </Link>
            ) : null}
          </div>
        </div>
      </article>

      {primarySponsor ? (
        <div className="relative overflow-hidden rounded-2xl border border-white/12 bg-stone-950/80">
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-200/90">
              Presented by
            </span>
            {match.isSponsored ? (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                Sponsored
              </span>
            ) : null}
          </div>
          {primarySponsor.link ? (
            <a
              href={primarySponsor.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 pb-3 hover:opacity-95 transition-opacity"
            >
              <img
                src={primarySponsor.url}
                alt="Sponsor"
                decoding="async"
                className="w-full max-h-36 sm:max-h-44 object-contain rounded-xl bg-white/5 p-3"
              />
            </a>
          ) : (
            <div className="px-3 pb-3">
              <img
                src={primarySponsor.url}
                alt="Sponsor"
                decoding="async"
                className="w-full max-h-36 sm:max-h-44 object-contain rounded-xl bg-white/5 p-3"
              />
            </div>
          )}
          {sponsoredImages.length > 1 ? (
            <div className="flex gap-2 px-3 pb-3 overflow-x-auto">
              {sponsoredImages.slice(1).map((img, idx) => {
                const entry = normalizeSponsoredImageEntry(img);
                if (!entry) return null;
                const thumb = (
                  <img
                    src={entry.url}
                    alt=""
                    className="h-10 sm:h-12 object-contain rounded-md bg-white/5 px-2 py-1 border border-white/10"
                  />
                );
                return entry.link ? (
                  <a
                    key={`${entry.url}-${idx}`}
                    href={entry.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 hover:opacity-90 transition-opacity"
                  >
                    {thumb}
                  </a>
                ) : (
                  <span key={`${entry.url}-${idx}`} className="shrink-0">
                    {thumb}
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const MatchCard = ({ match, featured = false }) => {
  const freeEnabled = match.freePredictionEnabled !== false;
  const marketEnabled = match.marketEnabled !== false;
  const { freeJackpot, boostJackpot } = jackpotPoolsFromItem(match);
  const status = effectiveEventStatus(match);
  const isLive = status === 'live' || status === 'active';

  return (
    <article className={`${CARD_BASE} ${featured ? 'ring-2 ring-red-600/15 dark:ring-red-500/20' : ''}`}>
      <div className={CARD_ACCENT} aria-hidden />
      <div className={CARD_INNER}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <CupChip item={match} />
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border ${statusBadgeClass(
              status
            )} ${isLive ? 'home-live-dot' : ''}`}
          >
            {isLive ? (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
            ) : null}
            {status || '—'}
          </span>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {match.teamAImage ? (
              <img src={match.teamAImage} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : null}
            <span className="font-bold text-gray-900 dark:text-white truncate text-sm sm:text-base">{match.teamA}</span>
          </div>
          <span className="text-[10px] font-bold text-gray-400 uppercase shrink-0">vs</span>
          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
            <span className="font-bold text-gray-900 dark:text-white truncate text-sm sm:text-base text-right">
              {match.teamB}
            </span>
            {match.teamBImage ? (
              <img src={match.teamBImage} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : null}
          </div>
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {stageNameOf(match) ? (
            <span className="font-medium text-gray-700 dark:text-gray-300">{stageNameOf(match)} · </span>
          ) : null}
          <span className="tabular-nums">{formatEventDateGmt(match.date) || '—'}</span>
        </div>

        <JackpotPoolsBanner freeJackpot={freeJackpot} boostJackpot={boostJackpot} compact className="mb-4" />

        <CardActions
          freeEnabled={freeEnabled}
          marketEnabled={marketEnabled}
          freeTo={`/match/${match._id}/free`}
          boostTo={`/match/${match._id}/boost`}
          marketTo={`/match/${match._id}/market`}
        />
      </div>
    </article>
  );
};

const PollCard = ({ poll, featured = false, impliedByMarketId = {} }) => {
  const freeEnabled = poll.freePredictionEnabled !== false;
  const marketEnabled = poll.marketEnabled !== false;
  const { top, hasMore } = rankPollOptionsByImplied(poll, impliedByMarketId, 3);
  const { freeJackpot, boostJackpot } = jackpotPoolsFromItem(poll);

  return (
    <article className={`${CARD_BASE} ${featured ? 'ring-2 ring-red-600/15 dark:ring-red-500/20' : ''}`}>
      <div className={CARD_ACCENT} aria-hidden />
      <div className={CARD_INNER}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <CupChip item={poll} />
          <span
            className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border ${statusBadgeClass(
              effectiveEventStatus(poll)
            )}`}
          >
            {effectiveEventStatus(poll) || '—'}
          </span>
        </div>

        <h3 className="font-bold text-gray-900 dark:text-white text-sm sm:text-base mb-2 line-clamp-2">
          {poll.question || poll.title || 'Poll'}
        </h3>

        {top?.length > 0 ? (
          <ul className="space-y-1.5 mb-3">
            {top.map((opt) => (
              <li key={opt.key || opt.text} className="flex justify-between text-xs gap-2">
                <span className="text-gray-700 dark:text-gray-300 truncate">{opt.text || opt.label}</span>
                <span className="tabular-nums font-semibold text-gray-900 dark:text-white shrink-0">
                  {Number.isFinite(opt.pct) ? `${opt.pct.toFixed(0)}%` : '—'}
                </span>
              </li>
            ))}
            {hasMore ? <li className="text-[10px] text-gray-400">+ more options</li> : null}
          </ul>
        ) : null}

        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 tabular-nums">
          {formatEventDateGmt(poll.date || poll.createdAt) || '—'}
        </div>

        <JackpotPoolsBanner freeJackpot={freeJackpot} boostJackpot={boostJackpot} compact className="mb-4" />

        <CardActions
          freeEnabled={freeEnabled}
          marketEnabled={marketEnabled}
          freeTo={`/poll/${poll._id}/free`}
          boostTo={`/poll/${poll._id}/boost`}
          marketTo={`/poll/${poll._id}/market`}
        />
      </div>
    </article>
  );
};

export default Home;
